/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';

import { KEY_ENTER } from '@gh/core-ui';

import { ColDef, ICellRendererParams, IRowNode } from 'ag-grid-community';
import { isFunction, noop, stubTrue } from 'lodash';

import { CellParams } from '../../../state';
import { GridCellRendererComponent } from '../../grid-component.interfaces';

@Component({
  standalone: false,
  selector: 'gh-grid-button-cell-renderer',
  templateUrl: 'grid-button-cell-renderer.component.html',
  styleUrls: ['grid-button-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridButtonCellRendererComponent implements OnDestroy, GridCellRendererComponent {
  editable: boolean;
  type: 'raised' | 'icon' | 'flat';
  icon?: string;
  label: string;

  private node: IRowNode;
  private colDef: ColDef;
  private _onClick: ProcedureFn<CellParams>;
  private isClickable: PredicateFn<CellParams>;
  private eGridCell: HTMLElement;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  agInit(params: ICellRendererParams): void {
    const label = params['label'];
    this._onClick = params['onClick'] || noop;
    this.type = params['type'] || 'flat';
    this.icon = params['icon'];
    this.isClickable = params['isClickable'] || stubTrue;
    this.colDef = params.colDef!;
    this.eGridCell = params.eGridCell;

    this.eGridCell.addEventListener('keydown', this.onKeyDown);

    this.label = isFunction(label) ? label(this.buildCellParams()) : label;
    this.refresh(params);
  }

  ngOnDestroy(): void {
    this.eGridCell.removeEventListener('keydown', this.onKeyDown);
  }

  refresh(params: ICellRendererParams): boolean {
    this.node = params.node;
    this.editable = this.isClickable(this.buildCellParams());
    this.changeDetectorRef.markForCheck();
    return true;
  }

  onClick(): void {
    this._onClick(this.buildCellParams());
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.which === KEY_ENTER) {
      this.onClick();
    }
  }

  private buildCellParams(): CellParams {
    return { node: this.node, data: this.node.data, colDef: this.colDef };
  }
}
